import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useFocusTrap(active: boolean, ownerId?: string) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;
    const root = containerRef.current;
    const previous = document.activeElement as HTMLElement | null;

    const getFocusable = () => {
      const owned = ownerId
        ? [...document.querySelectorAll<HTMLElement>(`[data-dialog-owner="${ownerId}"] ${FOCUSABLE}`)]
        : [];
      return [...root.querySelectorAll<HTMLElement>(FOCUSABLE), ...owned].filter(
        (el) => el.offsetParent !== null,
      );
    };

    const t = window.setTimeout(() => {
      const nodes = getFocusable();
      nodes[0]?.focus();
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (
        ownerId &&
        (document.activeElement as HTMLElement | null)?.closest(
          `[data-dialog-owner="${ownerId}"]`,
        )
      ) {
        return;
      }
      const nodes = getFocusable();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
      previous?.focus?.();
    };
  }, [active, ownerId]);

  return containerRef;
}
